import { useEffect } from 'react';

interface PrivacyPolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const sections = [
  {
    icon: 'ri-user-line',
    title: 'Information We Collect',
    content: [
      {
        label: 'Contact & Inquiry Data',
        text: 'When you submit a contact form, request a quote, or subscribe to our newsletter, we collect your name, email address, phone number, and company details as provided.',
      },
      {
        label: 'Usage Data',
        text: 'We automatically collect certain technical information when you visit our website, including IP address, browser type, pages viewed, time spent on pages, and referring URLs. This data helps us understand how our site is used and improve our services.',
      },
      {
        label: 'Business Information',
        text: 'In the course of providing logistics services, we may collect information about your shipments, delivery requirements, product specifications, and service preferences.',
      },
    ],
  },
  {
    icon: 'ri-settings-line',
    title: 'How We Use Your Information',
    content: [
      {
        label: 'Service Delivery',
        text: 'To process inquiries, provide quotes, coordinate logistics operations, and fulfil service agreements with clients and partners.',
      },
      {
        label: 'Communications',
        text: 'To respond to your questions, send operational updates, and — where you have opted in — deliver newsletters and promotional content about our services.',
      },
      {
        label: 'Legal & Regulatory Compliance',
        text: 'To meet our obligations under applicable Canadian federal and provincial law, including PIPEDA, food safety regulations, and transport legislation.',
      },
      {
        label: 'Improvement & Analytics',
        text: 'To analyse usage patterns, maintain website security, and enhance the overall quality and performance of our services.',
      },
    ],
  },
  {
    icon: 'ri-share-line',
    title: 'Information Sharing',
    content: [
      {
        label: 'No Sale of Personal Data',
        text: 'McALLISTER DELIGHT does not sell, rent, or trade your personal information to third parties for marketing purposes.',
      },
      {
        label: 'Service Providers',
        text: 'We may share data with trusted third-party service providers who assist in operating our website, conducting business, or servicing clients — provided those parties agree to keep this information confidential.',
      },
      {
        label: 'Legal Requirements',
        text: 'We may disclose information when required by law, regulatory authority, court order, or to protect the rights and safety of McALLISTER DELIGHT, its clients, and the public.',
      },
    ],
  },
  {
    icon: 'ri-lock-line',
    title: 'Data Security',
    content: [
      {
        label: 'Technical Safeguards',
        text: 'We implement industry-standard security measures including encrypted data transmission (SSL/TLS), access controls, and regular security assessments to protect your information against unauthorised access, disclosure, or misuse.',
      },
      {
        label: 'Data Retention',
        text: 'Personal information is retained only as long as necessary to fulfil the purposes for which it was collected, or as required by applicable law. Client records related to food transport operations are retained in accordance with CFIA and regulatory requirements.',
      },
    ],
  },
  {
    icon: 'ri-user-settings-line',
    title: 'Your Rights',
    content: [
      {
        label: 'Access & Correction',
        text: 'You have the right to access the personal information we hold about you and to request correction of any inaccuracies.',
      },
      {
        label: 'Withdrawal of Consent',
        text: 'Where processing is based on consent (e.g. newsletter subscription), you may withdraw consent at any time by clicking "Unsubscribe" in any email or by contacting us directly.',
      },
      {
        label: 'Complaints',
        text: 'If you believe your privacy rights have been violated, you may file a complaint with the Office of the Privacy Commissioner of Canada (OPC) at priv.gc.ca.',
      },
    ],
  },
  {
    icon: 'ri-cookie-line',
    title: 'Cookies & Tracking',
    content: [
      {
        label: 'Essential Cookies',
        text: 'Our website uses essential cookies necessary for site functionality. These cookies do not track personal information and cannot be disabled without affecting site performance.',
      },
      {
        label: 'Analytics Cookies',
        text: 'With your consent, we use analytics tools to understand how visitors interact with our website. You may manage cookie preferences through your browser settings at any time.',
      },
    ],
  },
];

export default function PrivacyPolicyModal({ isOpen, onClose }: PrivacyPolicyModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div
        className="relative bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative h-52 md:h-64 rounded-t-2xl overflow-hidden">
          <img
            src="https://public.readdy.ai/ai/img_res/ba53cc23d9b6c4836f45b7c71182eae0.jpg"
            alt="Privacy Policy — McALLISTER DELIGHT"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/85 via-[#0F172A]/40 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-8">
            <p className="text-[#DC2626] text-xs font-bold uppercase tracking-widest mb-2">
              LEGAL & COMPLIANCE
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight">
              Privacy Policy
            </h2>
          </div>
          <button
            onClick={onClose}
            className="absolute top-5 right-5 w-10 h-10 flex items-center justify-center bg-white/20 hover:bg-white/40 backdrop-blur-sm rounded-full text-white transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Body */}
        <div className="p-8 md:p-10">
          {/* Intro */}
          <div className="mb-8 p-5 rounded-xl bg-gray-50 border border-gray-100">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 flex items-center justify-center flex-shrink-0 bg-[#DC2626]/10 rounded-lg mt-0.5">
                <i className="ri-information-line text-[#DC2626] text-lg"></i>
              </div>
              <div>
                <p className="text-gray-600 text-sm leading-relaxed mb-2">
                  McALLISTER DELIGHT (<strong>"we"</strong>, <strong>"our"</strong>, or <strong>"the Company"</strong>) is committed to protecting the privacy and security of personal information in accordance with the <em>Personal Information Protection and Electronic Documents Act</em> (PIPEDA) and applicable provincial legislation.
                </p>
                <p className="text-gray-500 text-xs">
                  This Privacy Policy applies to all personal information collected through our website, logistics services, and business operations. <strong>Last updated: January 2025.</strong>
                </p>
              </div>
            </div>
          </div>

          {/* Sections */}
          <div className="space-y-8 mb-8">
            {sections.map((section, i) => (
              <div key={i}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 bg-[#1A365D] rounded-lg">
                    <i className={`${section.icon} text-white text-sm`}></i>
                  </div>
                  <h3 className="text-base font-bold text-[#1A365D]">
                    {i + 1}. {section.title}
                  </h3>
                </div>
                <div className="space-y-3 pl-11">
                  {section.content.map((item, j) => (
                    <div key={j} className="rounded-xl bg-gray-50 border border-gray-100 p-4">
                      <p className="text-xs font-bold text-gray-700 mb-1">{item.label}</p>
                      <p className="text-gray-500 text-xs leading-relaxed">{item.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Contact block */}
          <div className="rounded-2xl bg-[#1A365D] p-6 text-white">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 bg-white/15 rounded-xl">
                <i className="ri-mail-line text-white text-lg"></i>
              </div>
              <div>
                <h4 className="font-bold text-base mb-1">Privacy Inquiries</h4>
                <p className="text-gray-300 text-xs leading-relaxed mb-3">
                  For questions about this Privacy Policy, to exercise your rights, or to file a privacy concern, please contact our Privacy Officer directly.
                </p>
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center gap-2 text-xs text-gray-300">
                    <i className="ri-building-line text-[#DC2626]"></i>
                    <span>McALLISTER DELIGHT — Privacy Officer</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-300">
                    <i className="ri-map-pin-line text-[#DC2626]"></i>
                    <span>New Brunswick, Canada</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer note */}
          <p className="text-gray-400 text-[11px] text-center mt-6 leading-relaxed">
            McALLISTER DELIGHT reserves the right to update this Privacy Policy at any time. Material changes will be communicated via our website. Continued use of our services following any such update constitutes acceptance of the revised policy.
          </p>
        </div>
      </div>
    </div>
  );
}
